"use client";

import { usePathname } from "@/infrastructure/router/navigation";
import {
	type FormEvent,
	useCallback,
	useEffect,
	useId,
	useRef,
	useState,
} from "react";
import {
	AnalyticsAndImprovementsEnabledKey,
	ExperiencePreferencesChangedEvent,
	OpenFeedbackEvent,
	QuickFeedbackEnabledKey,
	readBooleanPreference,
} from "@/domain/feedback/AppExperiencePreferences";
import { invokeArtistRoute } from "@/infrastructure/supabase/functions";
import { feedbackCopyFor } from "@/i18n/FeedbackCopy";
import { useTranslation } from "@/i18n/ArtistI18n";

type FeedbackCategory =
	| "experience"
	| "bug"
	| "performance"
	| "content"
	| "account"
	| "billing"
	| "other";

interface PendingFeedback {
	readonly requestId: string;
	readonly installationId: string;
	readonly category: FeedbackCategory;
	readonly message: string;
	readonly appVersion: string;
	readonly buildNumber: string;
	readonly osVersion: string;
	readonly deviceModel: string;
	readonly locale: string;
	readonly route: string;
	readonly diagnostics: Record<string, string>;
	readonly allowsFollowUp: boolean;
	readonly contactEmail?: string;
}

const INSTALLATION_KEY = "artist.feedback.installation-id.v1";
const OUTBOX_KEY = "artist.feedback.outbox.v1";
const MAX_OUTBOX_COUNT = 20;

function localeCopy(locale: string) {
	const localized = feedbackCopyFor(locale);
	return {
		button: localized["feedback.title"],
		title: localized["feedback.title"],
		intro: localized["feedback.introduction"],
		category: localized["feedback.category.section"],
		details: localized["feedback.details.placeholder"],
		diagnostics: localized["feedback.diagnostics.toggle"],
		diagnosticsBody: localized["feedback.diagnostics.description"],
		followUp: localized["feedback.followUp.toggle"],
		email: localized["feedback.followUp.email"],
		submit: localized["feedback.submit"],
		close: localized["feedback.close"],
		submitted: localized["feedback.result.submitted"],
		queued: localized["feedback.result.queued"],
		categories: {
			experience: localized["feedback.category.experience"],
			bug: localized["feedback.category.bug"],
			performance: localized["feedback.category.performance"],
			content: localized["feedback.category.content"],
			account: localized["feedback.category.account"],
			billing: localized["feedback.category.billing"],
			other: localized["feedback.category.other"],
		},
	};
}

function installationId(): string {
	const existing = window.localStorage.getItem(INSTALLATION_KEY);
	if (existing && existing.length >= 16) return existing;
	const created = crypto.randomUUID();
	window.localStorage.setItem(INSTALLATION_KEY, created);
	return created;
}

function loadOutbox(): PendingFeedback[] {
	try {
		const raw = window.localStorage.getItem(OUTBOX_KEY);
		if (!raw) return [];
		const decoded = JSON.parse(raw) as unknown;
		return Array.isArray(decoded) ? (decoded as PendingFeedback[]) : [];
	} catch {
		return [];
	}
}

function saveOutbox(values: readonly PendingFeedback[]): void {
	if (values.length === 0) {
		window.localStorage.removeItem(OUTBOX_KEY);
		return;
	}
	window.localStorage.setItem(
		OUTBOX_KEY,
		JSON.stringify(values.slice(-MAX_OUTBOX_COUNT)),
	);
}

async function sendFeedback(payload: PendingFeedback): Promise<void> {
	const result = await invokeArtistRoute<{ readonly ok?: boolean }>("/feedback", {
		body: JSON.stringify(payload),
		timeoutMilliseconds: 15_000,
	});
	if (result.ok !== true) throw new Error("feedback-rejected");
}

async function flushOutbox(): Promise<void> {
	const pending = loadOutbox();
	if (pending.length === 0) return;
	const remaining: PendingFeedback[] = [];
	for (const payload of pending) {
		try {
			await sendFeedback(payload);
		} catch {
			remaining.push(payload);
		}
	}
	saveOutbox(remaining);
}

export function PersistentFeedback() {
	const pathname = usePathname();
	const { resolvedLocale } = useTranslation();
	const [open, setOpen] = useState(false);
	const [category, setCategory] = useState<FeedbackCategory>("experience");
	const [message, setMessage] = useState("");
	const [includeDiagnostics, setIncludeDiagnostics] = useState(true);
	const [allowsFollowUp, setAllowsFollowUp] = useState(false);
	const [contactEmail, setContactEmail] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [result, setResult] = useState<"submitted" | "queued" | null>(null);
	const [quickFeedbackEnabled, setQuickFeedbackEnabled] = useState(true);
	const [analyticsAndImprovementsEnabled, setAnalyticsAndImprovementsEnabled] =
		useState(true);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const dialogRef = useRef<HTMLDialogElement>(null);
	const titleId = useId();
	const copy = localeCopy(resolvedLocale);

	const close = useCallback(() => {
		dialogRef.current?.close();
		setOpen(false);
		triggerRef.current?.focus();
	}, []);

	useEffect(() => {
		const syncPreferences = () => {
			const quick = readBooleanPreference(QuickFeedbackEnabledKey, true);
			const analytics = readBooleanPreference(
				AnalyticsAndImprovementsEnabledKey,
				true,
			);
			setQuickFeedbackEnabled(quick);
			setAnalyticsAndImprovementsEnabled(analytics);
			if (!analytics) setIncludeDiagnostics(false);
		};
		const openComposer = () => {
			setResult(null);
			setOpen(true);
		};
		syncPreferences();
		window.addEventListener(ExperiencePreferencesChangedEvent, syncPreferences);
		window.addEventListener("storage", syncPreferences);
		window.addEventListener(OpenFeedbackEvent, openComposer);
		return () => {
			window.removeEventListener(
				ExperiencePreferencesChangedEvent,
				syncPreferences,
			);
			window.removeEventListener("storage", syncPreferences);
			window.removeEventListener(OpenFeedbackEvent, openComposer);
		};
	}, []);

	useEffect(() => {
		void flushOutbox();
		const online = () => void flushOutbox();
		window.addEventListener("online", online);
		return () => window.removeEventListener("online", online);
	}, []);

	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) return;
		if (open && !dialog.open) dialog.showModal();
		if (!open && dialog.open) dialog.close();
	}, [open]);

	const submit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const trimmed = message.trim();
		if (trimmed.length < 5 || trimmed.length > 4000 || submitting) return;
		setSubmitting(true);
		const payload: PendingFeedback = {
			requestId: crypto.randomUUID(),
			installationId: installationId(),
			category,
			message: trimmed,
			appVersion: process.env.NEXT_PUBLIC_APP_VERSION ?? "web",
			buildNumber: process.env.NEXT_PUBLIC_BUILD_NUMBER ?? "development",
			osVersion: navigator.userAgent.slice(0, 128),
			deviceModel: (navigator.platform || "Browser").slice(0, 128),
			locale: (navigator.language || "und").slice(0, 32),
			route: pathname.slice(0, 256),
			diagnostics: includeDiagnostics && analyticsAndImprovementsEnabled
				? {
					viewport: `${window.innerWidth}x${window.innerHeight}`,
					colorScheme: window.matchMedia("(prefers-color-scheme: dark)").matches
						? "dark"
						: "light",
					online: String(navigator.onLine),
				}
				: {},
			allowsFollowUp,
			...(allowsFollowUp && contactEmail.trim()
				? { contactEmail: contactEmail.trim() }
				: {}),
		};

		try {
			await sendFeedback(payload);
			setResult("submitted");
			void flushOutbox();
		} catch {
			const outbox = loadOutbox();
			if (!outbox.some((item) => item.requestId === payload.requestId)) {
				saveOutbox([...outbox, payload]);
			}
			setResult("queued");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			{quickFeedbackEnabled ? (
				<button
					ref={triggerRef}
					type="button"
					className="feedback-trigger"
					onClick={() => {
						setResult(null);
						setOpen(true);
					}}
					aria-haspopup="dialog"
				>
					<span aria-hidden="true">✦</span>
					<span>{copy.button}</span>
				</button>
			) : null}

			<dialog
				ref={dialogRef}
				className="feedback-dialog"
				aria-labelledby={titleId}
				onCancel={(event) => {
					event.preventDefault();
					close();
				}}
				onClose={() => setOpen(false)}
			>
				<form className="feedback-form" onSubmit={submit}>
					<div className="feedback-heading">
						<div>
							<h2 id={titleId}>{copy.title}</h2>
							<p>{copy.intro}</p>
						</div>
						<button type="button" className="feedback-close" onClick={close} aria-label={copy.close}>
							×
						</button>
					</div>

					<fieldset className="feedback-categories">
						<legend>{copy.category}</legend>
						{(Object.keys(copy.categories) as FeedbackCategory[]).map((value) => (
							<label key={value} className={category === value ? "is-selected" : ""}>
								<input
									type="radio"
									name="feedback-category"
									value={value}
									checked={category === value}
									onChange={() => setCategory(value)}
								/>
								<span>{copy.categories[value]}</span>
							</label>
						))}
					</fieldset>

					<label className="feedback-field">
						<span>{copy.details}</span>
						<textarea
							value={message}
							onChange={(event) => setMessage(event.target.value.slice(0, 4000))}
							minLength={5}
							maxLength={4000}
							required
							autoFocus
						/>
						<small>{message.length}/4000</small>
					</label>

					<label className="feedback-toggle">
						<input
							type="checkbox"
							checked={includeDiagnostics}
							disabled={!analyticsAndImprovementsEnabled}
							onChange={(event) => setIncludeDiagnostics(event.target.checked)}
						/>
						<span>
							<strong>{copy.diagnostics}</strong>
							<small>{copy.diagnosticsBody}</small>
						</span>
					</label>

					<label className="feedback-toggle">
						<input
							type="checkbox"
							checked={allowsFollowUp}
							onChange={(event) => setAllowsFollowUp(event.target.checked)}
						/>
						<span><strong>{copy.followUp}</strong></span>
					</label>

					{allowsFollowUp ? (
						<label className="feedback-field">
							<span>{copy.email}</span>
							<input
								type="email"
								value={contactEmail}
								onChange={(event) => setContactEmail(event.target.value.slice(0, 320))}
							/>
						</label>
					) : null}

					{result ? (
						<p className="feedback-result" role="status">
							{result === "submitted" ? copy.submitted : copy.queued}
						</p>
					) : null}

					<button
						type="submit"
						className="feedback-submit"
						disabled={message.trim().length < 5 || submitting || result !== null}
					>
						{submitting ? "…" : copy.submit}
					</button>
				</form>
			</dialog>
		</>
	);
}
